import {
  useQuery,
  useMutation,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";

import {
  app_content_api_requests,
  public_app_content_api_requests,
  APP_CONTENT_API,
} from "../../app-content";

import { BaseDto, FilterGroup, NormalFilter } from "../../util/baseDto";
import { useNotification } from "../../../components/common/GlobalNotification";
import { useMemo } from "react";

const PATH = "/content-pages";
const BASE_QUERY_KEY = APP_CONTENT_API + PATH;

export interface ContentPageItem extends BaseDto {
  id: number;
  category: string;
  subCategory: string;
  sectionText: string;
}

export interface ContentPageApiResponse {
  metaData: {
    size: number;
    totalPages: number;
    page: number;
    totalElements: number;
  };
  data: ContentPageItem[];
}

export interface CreateContentPageRequest {
  category: string;
  subCategory: string;
  sectionText: string;
}

export interface UpdateContentPageRequest {
  id: number;
  category: string;
  subCategory: string;
  sectionText: string;
}

export interface CreateContentPageResponse {
  id: number;
  category: string;
  subCategory: string;
  sectionText: string;
}

const useCreateContentPage = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async (
      request: CreateContentPageRequest
    ): Promise<CreateContentPageResponse> =>
      app_content_api_requests.post(PATH, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
      setSuccessNotification("Content Page created successfully");
    },
    onError: () => {
      setErrorNotification("Failed to create Content Page");
    },
  });
};

const useUpdateContentPage = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async ({
      id,
      request,
    }: {
      id: number;
      request: UpdateContentPageRequest;
    }): Promise<CreateContentPageResponse> =>
      app_content_api_requests.put(`${PATH}/${id}`, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
      setSuccessNotification("Content Page updated successfully");
    },
    onError: () => {
      setErrorNotification("Failed to update Content Page");
    },
  });
};

const useSearchContentPages = (
  size: number,
  page: number,
  keyword?: string
) => {
  const filterGroup: FilterGroup = useMemo(
    () => ({
      operator: "AND",
      criteria: [
        ...(keyword ? [NormalFilter("category", "LIKE", keyword)] : []),
      ],
    }),
    [keyword]
  );

  return useInfiniteQuery<ContentPageApiResponse>({
    queryKey: [BASE_QUERY_KEY, "paged", keyword, size],

    queryFn: async ({ pageParam = page }) =>
      public_app_content_api_requests.search(
        `${PATH}/public/search?page=${pageParam}&size=${size}&sort=category,asc`,
        filterGroup
      ),
    getNextPageParam: (lastPage) => {
      const currentPage = lastPage.metaData.page ?? 0;
      const totalPages = lastPage.metaData.totalPages ?? 0;
      return currentPage + 1 < totalPages ? currentPage + 1 : undefined;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

const useGetAllContentPages = () => {
  return useQuery({
    queryKey: [BASE_QUERY_KEY, "all"],
    queryFn: async (): Promise<ContentPageApiResponse> =>
      public_app_content_api_requests.get(`${PATH}/public/all?size=200`),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

const useGetContentPageById = (id: number) => {
  return useQuery({
    queryKey: [BASE_QUERY_KEY, id],

    queryFn: async (): Promise<ContentPageItem> =>
      app_content_api_requests.get(`${PATH}/${id}`),

    enabled: !!id,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};
const useDeleteContentPage = () => {
  const queryClient = useQueryClient();
  const { setSuccessNotification, setErrorNotification } = useNotification();

  return useMutation({
    mutationFn: async (id: number): Promise<void> =>
      app_content_api_requests.delete(`${PATH}/${id}`),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
      setSuccessNotification("Content page deleted successfully");
    },

    onError: () => {
      setErrorNotification("Failed to delete content page");
    },
  });
};

export {
  BASE_QUERY_KEY as CONTENT_PAGES_QUERY_KEY,
  useCreateContentPage,
  useUpdateContentPage,
  useSearchContentPages,
  useGetAllContentPages,
  useGetContentPageById,
  useDeleteContentPage,
};
